// Columna central de Vendedores: píldoras, conteo y la tabla; clave, NPI y comisión van al cajón.
import { Moon, Target, TrendingDown } from 'lucide-react';
import type { ReactNode } from 'react';
import { ClientLevelEnum } from '@fiado/type-kit/bin/loanScoring/enums/ClientLevelEnum';
import { Button } from '@/kit';
import { cn } from '@/lib/cn';
import { DataTable, UnavailableState, type DataTableColumn } from '@/shared/patterns/DataTable';
import { StatsBar, type StatsBarTone } from '@/shared/patterns/StatsBar';
import { NoSource } from '@/shared/patterns/NoSource';
import { OutlinePill, StatusPill, TierPill, TONE_OUTLINE_CLASS } from '@/shared/patterns/Pills';
import { reportPeriodLabel, type ReportPeriod } from '@/shared/patterns/PeriodBand';
import {
    daysWithoutSale,
    goalProgress,
    isInactive,
    NO_SALE_DAYS_THRESHOLD,
    pillCounts,
    sellerAlerts,
    type SellerContext,
    type SellerPill,
    type SellerRow,
    type SellerSort,
    type SellerSortKey,
    sortedRoles,
} from '../lib/sellersTable';
import { STORE_MANAGER_ROLE_ID } from '../lib/newSellerForm';
import { money } from '../lib/sellersFormat';

/** Ancho mínimo de la tabla; por debajo scrollea dentro de su columna en vez de comerse el detalle. */
const TABLE_MIN_WIDTH = '600px';

const PILLS: { key: SellerPill; label: string; tone: StatsBarTone; title: string }[] = [
    { key: 'all', label: 'Todos', tone: 'neutral', title: 'Todos los vendedores del padrón' },
    { key: 'active', label: 'Activos', tone: 'success', title: 'Vendedores activos en el padrón' },
    {
        key: 'top',
        label: 'Top 25%',
        tone: 'success',
        title: 'El cuartil superior por colocación entre quienes vendieron',
    },
    {
        key: 'attention',
        label: 'Necesitan atención',
        tone: 'danger',
        title: 'Más de 7 días sin venta o caída mayor al 20% vs la semana anterior',
    },
    {
        key: 'storeAdmin',
        label: 'Admin tienda',
        tone: 'info',
        title: 'Vendedores con el rol de admin de tienda en RBAC',
    },
];

/** Las tres primeras posiciones por colocación, con la píldora de metal. */
const MEDAL_LEVEL: Record<number, ClientLevelEnum> = {
    1: ClientLevelEnum.GOLD,
    2: ClientLevelEnum.SILVER,
    3: ClientLevelEnum.BRONZE,
};

export function SellerMedal({ rank }: { rank: number | undefined }) {
    if (rank === undefined || !(rank in MEDAL_LEVEL)) return null;
    return (
        <TierPill
            level={MEDAL_LEVEL[rank]}
            title={`Lugar ${String(rank)} en colocación de la red`}
            className="flex-none"
        />
    );
}

export const RBAC_UNAVAILABLE_REASON = 'RBAC no respondió: no se pudo leer el rol';

/** Nombres de los roles para el tooltip; `undefined` si no hay qué decir. */
export function rolesTitle(row: SellerRow): string | undefined {
    if (row.roles === null || row.roles.length === 0) return undefined;
    return sortedRoles(row.roles)
        .map((role) => role.name)
        .join(', ');
}

/** Píldoras de los roles RBAC; el admin de tienda va relleno para que destaque. */
export function SellerRolePills({ row }: { row: SellerRow }) {
    if (row.roles === null) return <NoSource reason={RBAC_UNAVAILABLE_REASON} />;
    if (row.roles.length === 0) {
        return (
            <span className="text-muted-foreground" title="Sin roles asignados en RBAC">
                —
            </span>
        );
    }
    return (
        <span className="flex min-w-0 items-center gap-0.5 overflow-hidden">
            {sortedRoles(row.roles).map((role) =>
                role.key === STORE_MANAGER_ROLE_ID ? (
                    <StatusPill key={role.key} tone="info">
                        {role.name}
                    </StatusPill>
                ) : (
                    <OutlinePill key={role.key} outline={TONE_OUTLINE_CLASS.neutral}>
                        {role.name}
                    </OutlinePill>
                ),
            )}
        </span>
    );
}

function AlertBadge({
    tone,
    title,
    children,
}: {
    tone: 'success' | 'muted' | 'warning';
    title: string;
    children: ReactNode;
}) {
    return (
        <span
            title={title}
            aria-label={title}
            role="img"
            className={cn(
                'inline-flex size-5 flex-none cursor-help items-center justify-center rounded',
                tone === 'success' && 'bg-success-soft text-success',
                tone === 'muted' && 'bg-muted text-muted-foreground',
                tone === 'warning' && 'bg-warning-soft text-warning',
            )}
        >
            {children}
        </span>
    );
}

/** Íconos de alerta junto al nombre; nada si el vendedor no tiene ninguna. */
function SellerAlertBadges({ row, context }: { row: SellerRow; context: SellerContext }) {
    const alerts = sellerAlerts(row, context);
    const badges: ReactNode[] = [];
    if (alerts.topNetwork)
        badges.push(
            <AlertBadge key="top" tone="success" title="Top 10% de la red">
                <Target className="size-3" aria-hidden />
            </AlertBadge>,
        );
    if (alerts.noSaleDays !== null)
        badges.push(
            <AlertBadge
                key="noSale"
                tone="muted"
                title={`${String(alerts.noSaleDays)} días sin venta`}
            >
                <Moon className="size-3" aria-hidden />
            </AlertBadge>,
        );
    if (alerts.drop)
        badges.push(
            <AlertBadge
                key="drop"
                tone="warning"
                title="Colocación cayó más del 20% vs semana anterior"
            >
                <TrendingDown className="size-3" aria-hidden />
            </AlertBadge>,
        );
    if (badges.length === 0) return null;
    return <span className="flex flex-none items-center gap-0.5">{badges}</span>;
}

function WeekOverWeekCell({ row, context }: { row: SellerRow; context: SellerContext }) {
    const pct = row.weekOverWeekChangePct;
    if (pct === null) {
        const days = daysWithoutSale(row, context.now);
        return days !== null && days <= NO_SALE_DAYS_THRESHOLD ? (
            <span
                className="text-[10px] font-semibold italic text-info"
                title="Sin ventas la semana anterior"
            >
                nuevo
            </span>
        ) : (
            <span className="text-muted-foreground" title="Sin ventas en los últimos 14 días">
                —
            </span>
        );
    }
    const rounded = Math.round(pct);
    if (Math.abs(pct) < 2) {
        return (
            <span className="text-muted-foreground" title="Sin cambio vs semana anterior">
                —
            </span>
        );
    }
    return rounded > 0 ? (
        <span className="text-[10px] font-bold text-success">▲ {rounded}%</span>
    ) : (
        <span className="text-[10px] font-bold text-danger">▼ {Math.abs(rounded)}%</span>
    );
}

function GoalCell({ row }: { row: SellerRow }) {
    const progress = goalProgress(row);
    if (progress === null) {
        return (
            <span
                className="text-[10.5px] text-muted-foreground"
                title="Sin meta para este periodo"
            >
                —
            </span>
        );
    }
    const pct = Math.round(progress * 100);
    return (
        <div
            className="flex flex-col gap-[3px]"
            title={`${money(row.placedCents)} de ${money(row.goalCents)}`}
        >
            <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                <div
                    className={cn(
                        'h-full rounded-full',
                        pct >= 100
                            ? 'bg-success'
                            : pct >= 80
                              ? 'bg-warning'
                              : pct >= 50
                                ? 'bg-[hsl(35_90%_55%)]'
                                : 'bg-danger',
                    )}
                    style={{ width: `${String(Math.min(pct, 100))}%` }}
                />
            </div>
            <span className="text-[10px] font-semibold tabular-nums">{pct}%</span>
        </div>
    );
}

export function SellersTable({
    rows,
    allRows,
    context,
    sort,
    onSortToggle,
    pill,
    onPillChange,
    selectedId,
    onSelect,
    isLoading,
    error,
    onRetry,
    period,
    rbacAvailable,
    actions,
}: {
    rows: SellerRow[];
    allRows: SellerRow[];
    context: SellerContext;
    sort: SellerSort;
    onSortToggle: (key: SellerSortKey) => void;
    pill: SellerPill;
    onPillChange: (pill: SellerPill) => void;
    selectedId: string | null;
    onSelect: (sellerId: string) => void;
    isLoading: boolean;
    error: Error | null;
    onRetry: () => void;
    period: ReportPeriod;
    /** Sin RBAC la píldora «Admin tienda» no tiene de dónde contar. */
    rbacAvailable: boolean;
    /** Botones de la barra de la tabla (p. ej. «+ Nuevo vendedor»). */
    actions?: ReactNode;
}) {
    const counts = pillCounts(allRows, context);
    const periodSub = reportPeriodLabel(period).toLowerCase();

    const columns: DataTableColumn<SellerRow, SellerSortKey>[] = [
        {
            key: 'name',
            label: 'Vendedor',
            sortKey: 'name',
            cellTitle: (row) => row.name,
            cell: (row) => (
                <span className="flex min-w-0 items-center gap-1">
                    <SellerMedal rank={context.ranks.get(row.sellerId)} />
                    <span className="truncate font-semibold">{row.name}</span>
                    {isInactive(row) ? (
                        <StatusPill tone="neutral" className="flex-none">
                            {row.status === 'SUSPENDED' ? 'Suspendido' : 'Inactivo'}
                        </StatusPill>
                    ) : null}
                    <SellerAlertBadges row={row} context={context} />
                </span>
            ),
        },
        {
            key: 'role',
            label: 'Rol',
            width: '76px',
            sortKey: 'role',
            note: 'Roles asignados en RBAC',
            cellTitle: rolesTitle,
            cell: (row) => <SellerRolePills row={row} />,
        },
        {
            key: 'store',
            label: 'Tienda',
            width: '80px',
            sortKey: 'store',
            cellTitle: (row) => row.storeName ?? undefined,
            cell: (row) => row.storeName ?? '—',
        },
        {
            key: 'placed',
            label: 'Colocación',
            note: `Colocación del periodo (${periodSub})`,
            width: '100px',
            numeric: true,
            sortKey: 'placed',
            cellClassName: () => 'font-bold',
            cell: (row) => money(row.placedCents),
        },
        {
            key: 'weekOverWeek',
            label: 'Δ sem',
            width: '64px',
            numeric: true,
            sortKey: 'weekOverWeek',
            note: 'Colocación de los últimos 7 días contra los 7 anteriores, sin importar el periodo',
            cell: (row) => <WeekOverWeekCell row={row} context={context} />,
        },
        {
            key: 'sales',
            label: 'Ventas',
            note: `Ventas del periodo (${periodSub})`,
            width: '74px',
            numeric: true,
            sortKey: 'sales',
            cell: (row) => row.salesCount ?? '—',
        },
        {
            key: 'goal',
            label: '% meta',
            note: 'Cumplimiento de la meta en pesos del periodo',
            width: '70px',
            sortKey: 'goal',
            cellOverflow: 'wrap',
            cell: (row) => <GoalCell row={row} />,
        },
    ];

    return (
        <>
            <StatsBar
                items={PILLS.map((p) => {
                    const noRoles = p.key === 'storeAdmin' && !rbacAvailable;
                    const blank = isLoading || error !== null || noRoles;
                    return {
                        key: p.key,
                        label: p.label,
                        tone: p.tone,
                        title: noRoles ? RBAC_UNAVAILABLE_REASON : p.title,
                        disabled: blank,
                        value: blank ? '—' : counts[p.key],
                    };
                })}
                active={pill}
                onChange={onPillChange}
                ariaLabel="Segmentos de vendedores"
            />
            <div className="flex flex-none flex-wrap items-center gap-x-3 gap-y-1 border-b border-border/50 px-3 py-1">
                <span className="text-[10.5px] font-semibold text-muted-foreground">
                    {isLoading || error
                        ? '— vendedores'
                        : `${String(rows.length)} ${rows.length === 1 ? 'vendedor' : 'vendedores'}`}
                </span>
                {actions ? (
                    <span className="ml-auto flex flex-wrap items-center justify-end gap-1.5">
                        {actions}
                    </span>
                ) : null}
            </div>

            {error ? (
                <UnavailableState
                    title="No se pudieron cargar los vendedores"
                    body={error.message}
                    action={
                        <Button variant="outline" size="sm" onClick={onRetry}>
                            Reintentar
                        </Button>
                    }
                />
            ) : (
                <DataTable
                    columns={columns}
                    rows={rows}
                    rowKey={(row) => row.sellerId}
                    sort={sort}
                    onSortToggle={onSortToggle}
                    minWidth={TABLE_MIN_WIDTH}
                    caption="Vendedores del periodo con alertas, rol, tienda, colocación, cambio semanal, ventas y cumplimiento de meta."
                    isRowSelected={(row) => row.sellerId === selectedId}
                    rowClassName={(row) => (isInactive(row) ? 'opacity-50' : undefined)}
                    onRowClick={(row) => {
                        onSelect(row.sellerId);
                    }}
                    isLoading={isLoading}
                    empty={
                        allRows.length === 0 ? (
                            'Tu cadena todavía no tiene vendedores dados de alta.'
                        ) : (
                            <span className="italic">
                                Sin vendedores con los filtros seleccionados.
                            </span>
                        )
                    }
                />
            )}
        </>
    );
}
